import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { canManageSuspension } from '../../utils/userSuspensionActions';
import { Settings } from 'lucide-react';
import { t } from '../../utils/i18n';
import { BAN_ACTION, UNBAN_ACTION, getBanAction, getUserId } from '../../utils/userBanActions';
import {
  REGISTER_PARTNER_CELEB_ACTION,
  UNREGISTER_PARTNER_CELEB_ACTION,
  getPartnerCelebAction,
} from '../../utils/partnerCelebActions';

/**
 * Row actions: gear toggles a dropdown below with view detail, ban/unban, and future actions.
 */
export default function UserRowActionsMenu({
  user,
  isOpen,
  onToggle,
  onClose,
  onViewDetail,
  onBanAction,
  onSuspensionAction,
  apiEnvironment,
  onChatRestriction,
  onDmRestriction,
  onPartnerCelebAction,
  canViewDetail = true,
  isBanPending = false,
  isPartnerCelebPending = false,
  pendingUserId = null,
  partnerCelebPendingUserId = null,
}) {
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);
  const userId = getUserId(user);
  const banAction = getBanAction(user);
  const partnerCelebAction = getPartnerCelebAction(user);
  const isRowBanPending = isBanPending && pendingUserId === userId;
  const isRowPartnerCelebPending = isPartnerCelebPending && partnerCelebPendingUserId === userId;
  const isRowPending = isRowBanPending || isRowPartnerCelebPending;

  const menuItems = [];

  if (canViewDetail) {
    menuItems.push({
      id: 'detail',
      label: t('user.actions.viewDetail'),
      onClick: () => {
        onClose();
        onViewDetail?.();
      },
    });
  }

  if (partnerCelebAction && onPartnerCelebAction) {
    menuItems.push({
      id: partnerCelebAction,
      label:
        partnerCelebAction === REGISTER_PARTNER_CELEB_ACTION
          ? t('user.partnerCeleb.register.button')
          : t('user.partnerCeleb.unregister.button'),
      onClick: () => {
        onClose();
        onPartnerCelebAction?.(user);
      },
      variant: partnerCelebAction === REGISTER_PARTNER_CELEB_ACTION ? 'partnerRegister' : 'partnerUnregister',
    });
  }

  if (onSuspensionAction && canManageSuspension(user, apiEnvironment)) {
    menuItems.push({ id: 'suspension', label: t('user.suspension.manage'), onClick: () => { onClose(); onSuspensionAction(user); }, variant: 'danger' });
  }
  if (onChatRestriction) {
    menuItems.push({ id: 'chatRestriction', label: t('user.chatRestriction.title'), onClick: () => { onClose(); onChatRestriction(user); } });
  }
  if (onDmRestriction) {
    menuItems.push({ id: 'dmRestriction', label: t('user.dmRestriction.manage'), onClick: () => { onClose(); onDmRestriction(user); } });
  }

  if (banAction) {
    menuItems.push({
      id: banAction,
      label: banAction === BAN_ACTION ? t('user.ban.button') : t('user.ban.unban.button'),
      onClick: () => {
        onClose();
        onBanAction?.(user);
      },
      variant: banAction === BAN_ACTION ? 'danger' : 'success',
    });
  }

  useEffect(() => {
    if (!isOpen || !btnRef.current) {
      setMenuPos(null);
      return undefined;
    }

    const updatePosition = () => {
      const rect = btnRef.current.getBoundingClientRect();
      const minWidth = Math.max(rect.width, 168);
      const viewportPadding = 8;
      // Align menu's right edge with the button — opens toward the left (Actions column is on the right).
      let left = rect.right - minWidth;
      if (left < viewportPadding) {
        left = viewportPadding;
      }
      const maxLeft = window.innerWidth - viewportPadding - minWidth;
      if (left > maxLeft) {
        left = Math.max(viewportPadding, maxLeft);
      }
      setMenuPos({
        top: rect.bottom + 4,
        left,
        minWidth,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocClick = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  if (menuItems.length === 0) {
    return <span className="text-dark-text-muted text-xs">—</span>;
  }

  const iconBtnClass = [
    'inline-flex items-center justify-center w-8 h-8 rounded-lg border transition-colors disabled:opacity-50',
    isOpen
      ? 'border-primary text-primary bg-primary/10'
      : 'border-primary/70 text-dark-text-secondary hover:text-primary hover:border-primary hover:bg-primary/5',
  ].join(' ');

  const menuPanel = isOpen && menuPos ? (
    <div
      ref={menuRef}
      role="menu"
      style={{
        position: 'fixed',
        top: menuPos.top,
        left: menuPos.left,
        minWidth: menuPos.minWidth,
      }}
      className="z-[100] flex flex-col py-1 rounded-lg border border-primary/60 bg-dark-card shadow-xl"
      onClick={(e) => e.stopPropagation()}
    >
      {menuItems.map((item) => {
        const isBanItem = item.id === BAN_ACTION || item.id === UNBAN_ACTION;
        const isPartnerItem =
          item.id === REGISTER_PARTNER_CELEB_ACTION || item.id === UNREGISTER_PARTNER_CELEB_ACTION;
        const isItemPending =
          (isBanItem && isRowBanPending) || (isPartnerItem && isRowPartnerCelebPending);
        const colorClass =
          item.variant === 'danger'
            ? 'text-red-400 hover:bg-red-500/10'
            : item.variant === 'success'
              ? 'text-green-400 hover:bg-green-500/10'
              : item.variant === 'partnerRegister'
                ? 'text-purple-300 hover:bg-purple-500/10'
                : item.variant === 'partnerUnregister'
                  ? 'text-amber-300 hover:bg-amber-500/10'
                  : 'text-dark-text hover:bg-dark-surface';
        return (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            disabled={isItemPending}
            onClick={item.onClick}
            className={`block w-full text-left px-3 py-2.5 text-sm whitespace-nowrap transition-colors disabled:opacity-50 ${colorClass}`}
          >
            {isItemPending
              ? isBanItem
                ? t('user.ban.processing')
                : t('user.partnerCeleb.processing')
              : item.label}
          </button>
        );
      })}
    </div>
  ) : null;

  return (
    <>
      <div className="inline-block text-left">
        <button
          ref={btnRef}
          type="button"
          aria-expanded={isOpen}
          aria-haspopup="menu"
          disabled={isRowPending}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className={iconBtnClass}
          title={t('user.actions.openMenu')}
          aria-label={t('user.actions.openMenu')}
        >
          <Settings size={16} aria-hidden />
        </button>
      </div>
      {menuPanel ? createPortal(menuPanel, document.body) : null}
    </>
  );
}
